import { prepare, layout } from "@chenglou/pretext"

const PRECISION = 0.1
/** Reference size for the (font-size-linear) word-width measurement. */
const MEASURE_REF_PX = 100
const BOX_LONG_WORD_MIN_FONT_SIZE = 36
/** Used when the container's line-height computes to "normal" (no numeric value to read). */
const DEFAULT_LINE_HEIGHT_RATIO = 1.2

/**
 * Line height as a multiple of font size, read from an element's computed style.
 * getComputedStyle resolves line-height to px (even for unitless CSS values), so we
 * divide by the current font size to recover the ratio. Falls back to a sensible
 * default when line-height is "normal" or either value is unavailable.
 */
export function lineHeightRatioOf(computed: CSSStyleDeclaration): number {
  const lineHeightPx = parseFloat(computed.lineHeight)
  const fontSizePx = parseFloat(computed.fontSize)
  if (Number.isFinite(lineHeightPx) && fontSizePx > 0) return lineHeightPx / fontSizePx
  return DEFAULT_LINE_HEIGHT_RATIO
}

export type FitMode = "box" | "oneline"

export interface FitTextParams {
  text: string
  fontFamily: string
  fontWeight: string
  containerWidth: number
  containerHeight: number
  minFs: number
  maxFs: number
  /** Line height as a multiple of font size, taken from the container's computed style. */
  lineHeightRatio: number
  mode: FitMode
}

let measureCanvas: HTMLCanvasElement | null = null

/**
 * Pretext needs `Intl.Segmenter` and `Array.prototype.at`. Some browsers we still
 * serve lack one or both (e.g. Firefox < 125, older Chromium-based mobile browsers),
 * and calling `prepare()` there throws. Those browsers use `layoutByWords` instead.
 */
function supportsPretext(): boolean {
  const segmenter = typeof Intl === "undefined" ? undefined : (Intl as { Segmenter?: unknown }).Segmenter
  return typeof segmenter === "function" && typeof (Array.prototype as { at?: unknown }).at === "function"
}

/** Detected on the first fit and cached. */
let pretextSupported: boolean | undefined

/**
 * Width (px) the text would occupy on a single, unwrapped line for `font`.
 * Returns 0 when measurement is unavailable (e.g. jsdom without a canvas backend),
 * which callers treat as "unconstrained" so behavior matches the pre-measurement code.
 */
function measureTextWidth(text: string, font: string): number {
  try {
    if (!measureCanvas) measureCanvas = document.createElement("canvas")
    const ctx = measureCanvas.getContext("2d")
    if (!ctx) return 0
    ctx.font = font
    return ctx.measureText(text).width
  } catch {
    return 0
  }
}

interface MeasuredWords {
  /** Per word: its width and the widths of its characters. */
  words: { width: number; charWidths: number[] }[]
  spaceWidth: number
}

/**
 * Widths (px) of each whitespace-separated word, its characters, and a single
 * space, at `MEASURE_REF_PX`. Text width is linear in font size, so callers scale these.
 */
function measureWordsAtRef(text: string, fontWeight: string, fontFamily: string): MeasuredWords {
  const refFont = `${fontWeight} ${MEASURE_REF_PX}px ${fontFamily}`
  return {
    words: text
      .split(/\s+/)
      .filter(Boolean)
      .map(word => ({
        width: measureTextWidth(word, refFont),
        charWidths: Array.from(word, char => measureTextWidth(char, refFont)),
      })),
    spaceWidth: measureTextWidth(" ", refFont),
  }
}

/**
 * Greedy word-wrap layout for browsers without Pretext support. A word wider
 * than the line is split between characters, like `overflow-wrap: break-word`.
 */
function layoutByWords(
  { words, spaceWidth }: MeasuredWords,
  fontSize: number,
  containerWidth: number,
  lineHeight: number,
): { lineCount: number; height: number } {
  const scale = fontSize / MEASURE_REF_PX
  const space = spaceWidth * scale
  let lineCount = 1
  let lineWidth = 0

  for (const word of words) {
    const width = word.width * scale
    if (lineWidth > 0 && lineWidth + space + width <= containerWidth) {
      lineWidth += space + width
      continue
    }
    if (lineWidth > 0) lineCount++
    if (width > containerWidth && containerWidth > 0) {
      lineWidth = 0
      for (const charRefWidth of word.charWidths) {
        const charWidth = charRefWidth * scale
        if (lineWidth > 0 && lineWidth + charWidth > containerWidth) {
          lineCount++
          lineWidth = 0
        }
        lineWidth += charWidth
      }
    } else {
      lineWidth = width
    }
  }

  return { lineCount, height: lineCount * lineHeight }
}

/**
 * Largest font size (px) at which the widest single word still fits within
 * `containerWidth`. Text width is linear in font size, so we measure once at a
 * reference size and scale. Returns Infinity when nothing constrains the width
 * (no words, or width measurement unavailable).
 *
 * This is what keeps moderately long words (e.g. "immediately") on one line:
 * the binary search is capped here so Pretext shrinks the font instead of
 * letting CSS break the word mid-word.
 */
function maxFontSizeForWidestWord(
  text: string,
  fontWeight: string,
  fontFamily: string,
  containerWidth: number
): number {
  const words = text.split(/\s+/).filter(Boolean)
  if (words.length === 0) return Infinity

  const refFont = `${fontWeight} ${MEASURE_REF_PX}px ${fontFamily}`
  let widestAtRef = 0
  for (const word of words) {
    const width = measureTextWidth(word, refFont)
    if (width > widestAtRef) widestAtRef = width
  }

  if (widestAtRef <= 0) return Infinity
  return (containerWidth * MEASURE_REF_PX) / widestAtRef
}

/**
 * Binary-searches the largest font size that fits `text` in the container,
 * mirroring Pretext's layout model. In "box" mode the upper bound is also
 * capped so the widest word fits on a single line.
 * `overflow-wrap: break-word` in CSS remains the backstop for words still
 * too wide at `minFs`.
 */
export function fitFontSize({
  text,
  fontFamily,
  fontWeight,
  containerWidth,
  containerHeight,
  minFs,
  maxFs,
  lineHeightRatio,
  mode,
}: FitTextParams): number {
  let lo = minFs
  let hi = maxFs

  // "oneline" already forces the whole string (and thus every word) onto a
  // single line, so the widest-word cap only matters for wrapped "box" titles.
  if (mode === "box") {
    const widthCap = maxFontSizeForWidestWord(text, fontWeight, fontFamily, containerWidth)
    if (widthCap < hi) {
      // A single over-wide word shouldn't drag the whole title down to `minFs`:
      // floor the width cap at `BOX_LONG_WORD_MIN_FONT_SIZE` and let `overflow-wrap: break-word`
      // break the word instead. The height path below still uses `minFs`, so
      // genuinely long (multi-line) text can keep shrinking all the way down.
      const wordFloor = Math.min(BOX_LONG_WORD_MIN_FONT_SIZE, hi)
      hi = Math.max(wordFloor, widthCap)
    }
  }

  pretextSupported ??= supportsPretext()
  const words = pretextSupported ? null : measureWordsAtRef(text, fontWeight, fontFamily)

  while (hi - lo > PRECISION) {
    const mid = (lo + hi) / 2
    const lineHeight = mid * lineHeightRatio
    const { lineCount, height } = words
      ? layoutByWords(words, mid, containerWidth, lineHeight)
      : layout(prepare(text, `${fontWeight} ${mid}px ${fontFamily}`), containerWidth, lineHeight)

    const fits = mode === "oneline" ? lineCount <= 1 : height <= containerHeight
    if (fits) {
      lo = mid
    } else {
      hi = mid
    }
  }

  return lo
}
